import React, { useState, useEffect } from 'react';
import { Table, Input, Tag, Typography, Card, Avatar } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import '../../Admin/Users/Users.css';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const Currencies: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageId, perPage]);

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (search) {
        params.search = search;
      }

      const response = await apiClient.get(API_ENDPOINTS.PUBLIC_GET_CURRENCIES, {
        params,
      });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setCurrencies(items);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total:
              paginator.itemCount ||
              paginator.totalCount ||
              paginator.total ||
              0,
          });
        } else {
          setPagination((prev) => ({
            ...prev,
            total: items.length,
          }));
        }
      }
    } catch (error: any) {
      console.error('Error fetching currencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('pageId', '1'); // Reset to first page on search
    setSearchParams(newParams);
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: 'Currency',
      key: 'currency',
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {record.iconUrl && (
            <Avatar src={record.iconUrl} size={32} />
          )}
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
              {record.symbol}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Exchange Rate (USD)',
      key: 'exchangeRate',
      render: (_: any, record: any) => {
        const oneUsdValue = Number(record.oneUsdValue);
        if (!oneUsdValue || isNaN(oneUsdValue)) return '-';
        return `$${(1 / oneUsdValue).toFixed(8)}`;
      },
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Currencies
        </Title>
      </HeaderContainer>

      <FiltersContainer>
        <Input
          placeholder="Search currencies..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ maxWidth: 300 }}
        />
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        {pagination.total > 0 && (
          <div style={{ marginBottom: 16, color: COLORS.textSecondary, fontSize: '14px' }}>
            Total: {pagination.total} currenc{pagination.total !== 1 ? 'ies' : 'y'}
          </div>
        )}
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={currencies}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total) => `Total ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            className="dark-table"
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>
    </PageContainer>
  );
};

export default Currencies;

